import { ExternalLinkIcon } from 'components/icons';
import { ReactComponent as GoIcon } from 'components/icons/icon-go.svg';
import { ReactComponent as InformationIcon } from 'components/icons/icon-information.svg';
import { CardBackground, Skeleton, Tooltip } from 'components/ui';
import { PAID_PLAN_TOOLTIP } from 'config.js';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from 'store';
import {
  useGetDashboardLifecycleQuery,
  useGetDashboardStoreAggQuery,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';

export default function LifecycleJourney() {
  const {
    storeSubscriptionPlan,
    subscriptionPlans,
  } = useStoreSubscriptionPlan();

  const { currentStore } = useAppSelector(userDetailsState);

  const { data: storeAgg } = useGetDashboardStoreAggQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const {
    data: lifecycleData,
    isFetching: loading,
  } = useGetDashboardLifecycleQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const segments = lifecycleData?.segments;
  const currency = currentStore.completeInfo?.currency;
  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  return (
    <section className="mt-7 md:mt-16">
      <h4 className="text-base tracking-wide md:text-2xl">Lifecycle journey</h4>
      <p className="text-black md:text-lg text-opacity-30">
        Manage your customer segments through their purchasing lifecycle as they
        move from active, to at risk, to becoming churned. Each box represents
        the number of customers who fall under each stage.
      </p>
      <a
        href="https://help.tresl.co/en/articles/4317318-what-is-the-lifecycle-journey"
        className="flex items-start space-x-0.5 text-segments-link mt-2 font-bold"
        target="_blank"
        rel="noreferrer"
      >
        <p>Learn more</p>
        <ExternalLinkIcon className="inline w-5 h-5 align-middle" />
      </a>
      <div className="mt-2 -mx-segments-10px md:mx-0 md:mt-6 ">
        <CardBackground>
          <div className="mx-5 my-6 lg:my-10 lg:ml-10 lg:mr-12">
            <div className="overflow-x-auto">
              <table
                className="w-full border-separate table-auto lg:overflow-hidden"
                style={{ borderSpacing: '14px 0' }}
              >
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-white"></th>
                    <ColumnHeader
                      title="Active customers"
                      color="active"
                      currency={currency}
                    />
                    <ColumnHeader
                      title="At-risk customers"
                      color="risk"
                      currency={currency}
                    />
                    <ColumnHeader
                      title="Churned customers"
                      color="churned"
                      currency={currency}
                    />
                  </tr>
                </thead>
                <tbody>
                  {/* New signups - 0 orders */}
                  <tr>
                    <RowHeader title="New signups" subtitle="No orders" />
                    <TableCell
                      segmentSize={
                        segments?.active_new_signups?.segment_size ?? 0
                      }
                      segmentName={segments?.active_new_signups?.segment_name}
                      potential={
                        (segments?.active_new_signups?.segment_size ?? 0) *
                        ((segments?.active_new_signups?.aclv ?? 0) -
                          (segments?.active_new_signups?.arpu ?? 0))
                      }
                      timeframe={`<= ${storeAgg?.first_growth ?? 0}`}
                      color="active"
                      opacity="0.4"
                      rounded="top"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={
                        segments?.at_risk_new_signups?.segment_size ?? 0
                      }
                      segmentName={segments?.at_risk_new_signups?.segment_name}
                      potential={
                        (segments?.at_risk_new_signups?.segment_size ?? 0) *
                        ((segments?.at_risk_new_signups?.aclv ?? 0) -
                          (segments?.at_risk_new_signups?.arpu ?? 0))
                      }
                      timeframe={`${storeAgg?.first_growth ?? 0} – ${
                        storeAgg?.first_choke ?? 0
                      }`}
                      color="risk"
                      opacity="0.4"
                      rounded="top"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={segments?.churned_signups?.segment_size ?? 0}
                      segmentName={segments?.churned_signups?.segment_name}
                      potential={
                        (segments?.churned_signups?.segment_size ?? 0) *
                        ((segments?.churned_signups?.aclv ?? 0) -
                          (segments?.churned_signups?.arpu ?? 0))
                      }
                      timeframe={`> ${storeAgg?.first_choke ?? 0}`}
                      color="churned"
                      opacity="0.4"
                      rounded="top"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                  </tr>
                  {/* One Timers - 1 order */}
                  <tr>
                    <RowHeader title="One timers" subtitle="1 order" />
                    <TableCell
                      segmentSize={
                        segments?.active_one_timers?.segment_size ?? 0
                      }
                      segmentName={segments?.active_one_timers?.segment_name}
                      potential={
                        (segments?.active_one_timers?.segment_size ?? 0) *
                        ((segments?.active_one_timers?.aclv ?? 0) -
                          (segments?.active_one_timers?.arpu ?? 0))
                      }
                      timeframe={`<= ${storeAgg?.second_growth ?? 30}`}
                      color="active"
                      opacity="0.3"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={
                        segments?.at_risk_one_timers?.segment_size ?? 0
                      }
                      segmentName={segments?.at_risk_one_timers?.segment_name}
                      potential={
                        (segments?.at_risk_one_timers?.segment_size ?? 0) *
                        ((segments?.at_risk_one_timers?.aclv ?? 0) -
                          (segments?.at_risk_one_timers?.arpu ?? 0))
                      }
                      timeframe={`${storeAgg?.second_growth ?? 30} – ${
                        storeAgg?.second_choke ?? 60
                      }`}
                      color="risk"
                      opacity="0.3"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={
                        segments?.churned_one_timers?.segment_size ?? 0
                      }
                      segmentName={segments?.churned_one_timers?.segment_name}
                      potential={
                        (segments?.churned_one_timers?.segment_size ?? 0) *
                        ((segments?.churned_one_timers?.aclv ?? 0) -
                          (segments?.churned_one_timers?.arpu ?? 0))
                      }
                      timeframe={`> ${storeAgg?.second_choke ?? 60}`}
                      color="churned"
                      opacity="0.3"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                  </tr>
                  {/* Repeats - 2 Orders */}
                  <tr>
                    <RowHeader title="Repeats" subtitle="2 orders" />
                    <TableCell
                      segmentSize={segments?.active_repeats?.segment_size ?? 0}
                      segmentName={segments?.active_repeats?.segment_name}
                      potential={
                        (segments?.active_repeats?.segment_size ?? 0) *
                        ((segments?.active_repeats?.aclv ?? 0) -
                          (segments?.active_repeats?.arpu ?? 0))
                      }
                      timeframe={`<= ${storeAgg?.third_growth ?? 60}`}
                      color="active"
                      opacity="0.2"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={segments?.at_risk_repeats?.segment_size ?? 0}
                      segmentName={segments?.at_risk_repeats?.segment_name}
                      potential={
                        (segments?.at_risk_repeats?.segment_size ?? 0) *
                        ((segments?.at_risk_repeats?.aclv ?? 0) -
                          (segments?.at_risk_repeats?.arpu ?? 0))
                      }
                      timeframe={`${storeAgg?.third_growth ?? 60} – ${
                        storeAgg?.third_choke ?? 90
                      }`}
                      color="risk"
                      opacity="0.2"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={segments?.churned_repeats?.segment_size ?? 0}
                      segmentName={segments?.churned_repeats?.segment_name}
                      potential={
                        (segments?.churned_repeats?.segment_size ?? 0) *
                        ((segments?.churned_repeats?.aclv ?? 0) -
                          (segments?.churned_repeats?.arpu ?? 0))
                      }
                      timeframe={`> ${storeAgg?.third_choke ?? 90}`}
                      color="churned"
                      opacity="0.2"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                  </tr>
                  {/* Loyals - 3+ orders */}
                  <tr>
                    <RowHeader title="Loyals" subtitle="3+ orders" />
                    <TableCell
                      segmentSize={segments?.active_loyals?.segment_size ?? 0}
                      segmentName={segments?.active_loyals?.segment_name}
                      potential={
                        (segments?.active_loyals?.segment_size ?? 0) *
                        ((segments?.active_loyals?.aclv ?? 0) -
                          (segments?.active_loyals?.arpu ?? 0))
                      }
                      timeframe={`<= ${storeAgg?.ipt ?? 90} `}
                      color="active"
                      opacity="0.1"
                      rounded="bottom"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={segments?.at_risk_loyals?.segment_size ?? 0}
                      segmentName={segments?.at_risk_loyals?.segment_name}
                      potential={
                        (segments?.at_risk_loyals?.segment_size ?? 0) *
                        ((segments?.at_risk_loyals?.aclv ?? 0) -
                          (segments?.at_risk_loyals?.arpu ?? 0))
                      }
                      timeframe={`${storeAgg?.ipt ?? 90} – ${
                        (storeAgg?.ipt && storeAgg.ipt * 2) ?? 180
                      } `}
                      color="risk"
                      opacity="0.1"
                      rounded="bottom"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                    <TableCell
                      segmentSize={segments?.churned_loyals?.segment_size ?? 0}
                      segmentName={segments?.churned_loyals?.segment_name}
                      potential={
                        (segments?.churned_loyals?.segment_size ?? 0) *
                        ((segments?.churned_loyals?.aclv ?? 0) -
                          (segments?.churned_loyals?.arpu ?? 0))
                      }
                      timeframe={`> ${
                        (storeAgg?.ipt && storeAgg.ipt * 2) ?? 180
                      } `}
                      color="churned"
                      opacity="0.1"
                      rounded="bottom"
                      currency={currency}
                      loading={loading}
                      locale={locale}
                    />
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Tooltip
                text={PAID_PLAN_TOOLTIP}
                disabled={
                  storeSubscriptionPlan >= subscriptionPlans.small_business
                }
              >
                <Link
                  to={{
                    pathname: '/analytics/purchase-timing',
                    search: currentStore.serializedStoreName,
                  }}
                  className={`flex items-center justify-center mt-5 mr-4 space-x-1 font-bold text-segments-link text-segments-13px lg:justify-end ${
                    storeSubscriptionPlan < subscriptionPlans.small_business &&
                    'pointer-events-none opacity-50'
                  }`}
                >
                  View lifecycle insights
                  <GoIcon />
                </Link>
              </Tooltip>
            </div>
          </div>
        </CardBackground>
      </div>
    </section>
  );
}

interface RowHeaderProps {
  title: string;
  subtitle: string;
}
function RowHeader({ title, subtitle }: RowHeaderProps) {
  return (
    <th
      scope="row"
      className="sticky left-0 pr-2 font-normal text-left bg-white md:text-lg h-28 lg:pr-5 "
    >
      <p className="leading-4">{title}</p>
      <p className="truncate opacity-30">{subtitle}</p>
    </th>
  );
}

interface ColumnHeaderProps {
  title: string;
  color: 'active' | 'risk' | 'churned';
  currency: string | undefined;
}
function ColumnHeader({ title, color, currency }: ColumnHeaderProps) {
  const textColor = {
    active: 'text-segments-active',
    risk: 'text-segments-at-risk',
    churned: 'text-segments-churned-title',
  };

  return (
    <th scope="col" className="font-normal md:text-lg text-left pb-3.5 pl-4">
      <p className={`${textColor[color]} leading-4`}>{title}</p>
      <div className="flex space-x-2">
        <span className="flex items-center w-full text-base space-x-0.5">
          <p className="truncate opacity-30">
            Potential <span>{currency}</span>
          </p>
          <Tooltip text="Total CLV - total spent">
            <div className=" hover:bg-segments-gray rounded p-0.5">
              <InformationIcon />
            </div>
          </Tooltip>
        </span>
        <span className="flex items-center space-x-0.5">
          <p className="truncate opacity-30">Time frame</p>
          <Tooltip text="Number of days since signup or last order">
            <div className=" hover:bg-segments-gray rounded p-0.5">
              <InformationIcon />
            </div>
          </Tooltip>
        </span>
      </div>
    </th>
  );
}

interface TableCellProps {
  segmentSize?: number;
  potential?: number;
  timeframe: number | string;
  color: 'active' | 'risk' | 'churned';
  opacity: '0.4' | '0.3' | '0.2' | '0.1';
  rounded?: 'top' | 'bottom';
  currency: string | undefined;
  locale: string;
  loading: boolean;
  segmentName: string | undefined;
}

function TableCell({
  segmentSize,
  potential,
  timeframe,
  color,
  opacity,
  rounded,
  currency,
  locale,
  loading,
  segmentName,
}: TableCellProps) {
  const location = useLocation();
  const { currentStore } = useAppSelector(userDetailsState);

  const bgColor = {
    active: 'bg-segments-active',
    risk: 'bg-segments-at-risk',
    churned: 'bg-segments-churned',
  };

  const bgColorOpacity = {
    0.4: 'bg-opacity-40',
    0.3: 'bg-opacity-30',
    0.2: 'bg-opacity-20',
    0.1: 'bg-opacity-10',
  };

  const borderRounded = {
    top: 'rounded-t-lg',
    bottom: 'rounded-b-lg',
  };
  return (
    <td
      className={`${bgColor[color]} ${bgColorOpacity[opacity]} p-4 ${
        rounded && borderRounded[rounded]
      }`}
    >
      <Skeleton isLoaded={!loading}>
        <p className="leading-9 text-black text-segments-32px h-9">
          {segmentSize?.toLocaleString(locale)}
        </p>
      </Skeleton>
      <Skeleton isLoaded={!loading}>
        <div className="flex justify-between h-4 mt-2 text-base leading-4 text-black text-opacity-30">
          <p>
            {potential?.toLocaleString(locale, {
              style: 'currency',
              currency: currency ?? 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
          <p>{timeframe} days</p>
        </div>
      </Skeleton>
      <Link
        to={{
          pathname: '/dashboard/playbook',
          state: { background: location },
          search:
            currentStore.serializedStoreName +
            `&segment_name=${segmentName?.replace(/\s/g, '_').toLowerCase()}`,
        }}
      >
        <p className="mt-2 font-bold leading-4 text-segments-13px text-segments-link">
          View playbook
        </p>
      </Link>
    </td>
  );
}
